import { LoaderFunction } from 'react-router-dom';
import { responseHandler } from '../../../shared/responseHandler/responseHandler';
import { map, take, zipWith } from 'rxjs';
import {
  DictOneTimeCustomersQueryRes,
  createOrderService,
} from '../../order/services/createOrder.service';
import { ListCustomersRes, customerService } from '../../customer/services/customer.service';
import { ArticlesListRes, articleService } from '../../article/services/article.service';
import { IItem as IFilterDictItem } from '../../../shared/components/filterTableWithModal/filterTableWithModal.component';
import { orderStatisticService } from '../services/orderStatistic.service';
import { storageHelper } from '../../../shared/helpers/storage';
import { IOrderStatisticState, initOrderStatisticState } from '../order/orderStatistic.state';
import {
  configsData,
  DictSubCompaniesRes,
} from '../../../shared/services/configsData/configsData.service';
import { getI18n } from 'react-i18next';

export const orderStatisticLoader: LoaderFunction = async (): Promise<IOrderStatisticLoader> => {
  const initData = structuredClone(initOrderStatisticState);

  initData.profileOptions = storageHelper.local.getItem('orderStatistic.profiles') || [];
  initData.profileId = storageHelper.local.getItem('orderStatistic.selectedProfileId') || undefined;
  if (initData.profileId) {
    const sessionProfileFilter = initData?.profileOptions?.find(
      (el) => el.id === initData.profileId,
    );
    if (sessionProfileFilter) {
      initData.filter = sessionProfileFilter.settings.filter;
      initData.groupBy = initData.filter.groupByForFutureCalculations;
      initData.gridGrouping = sessionProfileFilter.settings.gridGrouping || [];
    }
  }

  const { oneTimeCustomers, customers, articles, subCompanies } =
    await orderStatisticService.globHelpers.streamToPromise(
      createOrderService.sub.oneTimeCustomerList().pipe(
        zipWith(
          customerService.sub.listGridCustomers(),
          articleService.sub.articleList(),
          configsData.sub.dictSubCompanies(),
        ),
        responseHandler<
          [DictOneTimeCustomersQueryRes, ListCustomersRes, ArticlesListRes, DictSubCompaniesRes]
        >({
          errorReturnType: [[], [], [], []],
        }),
        take(1),
        map(([oneTimeCustomersRaw, customersRaw, articlesRaw, subCompaniesRaw]) => {
          const oneTimeCustomers = oneTimeCustomersRaw.map(({ id, label, customerNo }) => ({
            id,
            number: customerNo,
            description: label ?? '',
          }));

          const customers = customersRaw.map(({ id, customerNo, internalOrFullName }) => ({
            id,
            number: customerNo,
            description: internalOrFullName ?? '',
          }));

          const articles = articlesRaw.map(({ id, articleNo, description }) => ({
            id,
            number: articleNo,
            description: description ?? '',
          }));

          const subCompanies = subCompaniesRaw.map(({ id, label }) => ({
            id,
            number: '',
            description: label ?? '',
          }));

          // Add record with id = 'null' for filtering items without assigned subCompany
          subCompanies.unshift({
            id: 'null',
            number: '',
            description: getI18n().t('common.default'),
          });

          return { oneTimeCustomers, customers, articles, subCompanies };
        }),
      ),
      () => {
        createOrderService.pub.oneTimeCustomerList();
        customerService.pub.listGridCustomers({ filter: { isActive: true }, cache: false });
        articleService.pub.articlesList({ filter: { isActive: true }, cache: false });
        configsData.pub.common(['dictSubCompanies']);
      },
    );

  return {
    initData,
    dicts: {
      oneTimeCustomers,
      customers,
      articles,
      subCompanies,
    },
  };
};

export interface IDict {
  oneTimeCustomers: IFilterDictItem[];
  customers: IFilterDictItem[];
  articles: IFilterDictItem[];
  subCompanies: IFilterDictItem[];
}

export interface IOrderStatisticLoader {
  initData: IOrderStatisticState;
  dicts: IDict;
}
